/**
 * Global application constants
 * Centralized values for dates, formats, defaults, and other app-wide settings
 */

// Date range filter defaults
export const DATE_RANGE_DEFAULTS = {
  last14Days: 'last_14_days',
} as const;

export type DateRangeDefault = (typeof DATE_RANGE_DEFAULTS)[keyof typeof DATE_RANGE_DEFAULTS];

// Date and time formats
export const DATE_FORMATS = {
  display: "MMM dd, yyyy",
  input: "yyyy-MM-dd",
  timestamp: "yyyy-MM-dd HH:mm:ss",
  time: "HH:mm",
  dateTime: "MMM dd, yyyy HH:mm",
} as const;

export type DateFormat = (typeof DATE_FORMATS)[keyof typeof DATE_FORMATS];

// Pagination defaults
export const PAGINATION = {
  defaultPageSize: 20,
  pageSizeOptions: [10, 20, 50, 100],
  maxPageSize: 100,
} as const;

// API defaults
export const API_DEFAULTS = {
  /**
   * Global Axios HTTP timeout for all requests (milliseconds).
   * Configured once in src/shared/api/client.ts — do not add per-request overrides.
   */
  httpTimeout: 120000, // 2 minutes
  retryAttempts: 3,
  retryDelay: 1000, // 1 second
} as const;

// UI constants
export const UI_CONSTANTS = {
  debounceDelay: 300,
  animationDuration: 200,
  toastDuration: 5000,
  supportedImageTypes: ["image/jpeg", "image/png", "image/webp"],
} as const;

// Vehicle status mappings with type-safe keys
export const VEHICLE_STATUS = {
  active: "active",
  maintenance: "maintenance",
  inactive: "inactive",
} as const;

export type VehicleStatus =
  (typeof VEHICLE_STATUS)[keyof typeof VEHICLE_STATUS];

// Vehicle types
export const VEHICLE_TYPES = {
  0: 'Bus',
  1: 'Passenger Car',
  2: 'Van',
} as const

export type VehicleType = keyof typeof VEHICLE_TYPES

// Dashboard trend types
export const TREND_TYPES = {
  up: "up",
  down: "down",
  neutral: "neutral",
} as const;

export type TrendType = (typeof TREND_TYPES)[keyof typeof TREND_TYPES];

export const DISPATCH_TYPE = {
  MDT: 4,
  Manual: 9,
} as const;

export const DISPATCH_TYPES = {
  0: 'Old MDT',
  1: 'Manual',
  2: 'Manual',
  [DISPATCH_TYPE.MDT]: 'MDT',
  [DISPATCH_TYPE.Manual]: 'Manual',
} as const;

/** Only these dispatch types may be chosen when creating / editing a vehicle (RoR collection_select: [4, 9]). */
export const DISPATCH_TYPE_SELECTABLE = [DISPATCH_TYPE.MDT, DISPATCH_TYPE.Manual] as const;

/**
 * Large wheelchair conversion ratios — mirrors legacy Rails Vehicle::LARGE_WC_RATIOS constant.
 * Used as the selectable options for the wc2_to_wc (Large wheelchair ratio) field.
 */
export const LARGE_WC_RATIOS = [1.0, 1.25, 1.5, 1.75, 2.0, 2.25, 2.5, 2.75, 3.0] as const;

/** Dropdown options for the Dispatch type field on run edit form (MDT + Manual only). */
export const DISPATCH_TYPE_OPTIONS = [
  { value: DISPATCH_TYPE.MDT, label: 'MDT' },
  { value: DISPATCH_TYPE.Manual, label: 'Manual' },
] as const;

/**
 * Labels for ALL known dispatch type values including legacy "Old MDT" (0).
 * Used for read-only display — value 0 may exist in persisted run data.
 */
export const DISPATCH_TYPE_LABELS: Record<number, string> = {
  0: 'Old MDT',
  [DISPATCH_TYPE.MDT]: 'MDT',
  [DISPATCH_TYPE.Manual]: 'Manual',
};

// Map defaults
export const MAP_CONSTANTS = {
  defaultZoom: 12,
  minZoom: 8,
  maxZoom: 18,
  clusterThreshold: 50,
  updateInterval: 3000, // 3 seconds
  markerSizes: {
    small: 20,
    medium: 30,
    large: 40,
  },
} as const;

// Theme and styling constants
export const THEME_CONSTANTS = {
  breakpoints: {
    sm: 640,
    md: 768,
    lg: 1024,
    xl: 1280,
    "2xl": 1536,
    },
    zIndex: {
      dropdown: 1000,
      sticky: 1020,
      fixed: 1030,
      modal: 1040,
      popover: 1050,
      tooltip: 1060,
    },
  } as const;
  
  export const FLEET_TYPES = {
    1: "Overflow",
    2: "Dedicated",
    3: "Service Line",
    4: "Reserve",
    5: "Archive",
  } as const;
  
  export const PREFERENCE_TYPES = {
    6: 'Default',
  } as const;
  
  export const SKILL_TYPES = {
    3: 'Average',
  } as const;
  
  export const AVAILABILITY_TYPES = {
    0: 'Normal',
    1: 'Unavailable',
    4: 'Break',
  } as const;

  // Map UI column field names to their API (DB) field names for server-side sorting (Subscriptions)
  export const SUBSCRIPTION_SORT_FIELD_MAP: Record<string, string> = {
    subscription: 'id',
    target: 'id',
    start: 'span_start',
    end: 'span_end',
    days: 'id',
    tripType: 'willcall_status',
    status: 'status',
    pickupRequested: 'requested_pickup',
    pickupNegotiated: 'promised_pickup',
    dropoffRequested: 'requested_dropoff',
    client: 'customer.name',
    device: 'id',
    pickupLocation: 'start_location.name',
    dropoffLocation: 'end_location.name',
    funding: 'major_funding_source.name',
    billingCodeSetName: 'billing_code_set.id',
    tripGroup: 'trip_group.name',
    provider: 'transportation_provider.name',
  };

/** Maps break float_type (1=Template, 2=Float, 3=Shifted) to display abbreviations. */
export const RUN_STATUS_BREAK_FLOAT_TYPE_ABBR: Record<number, string> = {
  1: "T",
  2: "F",
  3: "S",
};

// ---------------------------------------------------------------------------
// Leg Map (KML) style constants
// ---------------------------------------------------------------------------

/** Polyline style definition decoded from the leg.kml AABBGGRR colour spec. */
export interface KmlStyleDef {
  color: string;
  opacity: number;
  width: number;
}

/**
 * Colours decoded from the leg.kml external style sheet (AABBGGRR → CSS #RRGGBB):
 *   route-line:   7fff0000 → #0000ff at 50% opacity (scheduled route — blue)
 *   segment-line: 7fcc00cc → #cc00cc at 50% opacity (sub-segment/detour — purple)
 *   track:        bfe4ab45 → #45abe4 at 75% opacity (GPS track — sky blue)
 */
export const LEG_MAP_STYLE_COLORS: Record<string, KmlStyleDef> = {
  'route-line':   { color: '#0000ff', opacity: 0.50, width: 4 },
  'segment-line': { color: '#cc00cc', opacity: 0.50, width: 5 },
  'track':        { color: '#45abe4', opacity: 0.75, width: 4 },
};

/**
 * Fallback colours for placemarks with no recognised styleId:
 *   index 0 → route-line (blue), index 1+ → segment-line (purple)
 */
export const LEG_MAP_FALLBACK_COLORS: string[] = ['#0000ff', '#cc00cc'];